/**
 * Wrapper côté front pour les Edge Functions multi-portails.
 *
 * - `invokePortalPublish` appelle l'Edge Function `portal-publish` avec
 *   le JWT courant (Supabase l'injecte automatiquement).
 * - `fetchLatestPortalPublications` lit la dernière row par portail pour un bien.
 */

import { supabase } from "@/lib/supabase";
import type { PortalId } from "@/lib/portals";

export type PortalAction = "publish" | "update" | "deactivate" | "delete";

export interface PortalPublishResponse {
  reference_id: string;
  status: string;
  portal_publication_id: string;
  portal: PortalId;
  noop?: boolean;
}

export interface PortalPublicationRow {
  id: string;
  bien_id: string;
  portal: PortalId;
  external_id: string;
  reference_id: string | null;
  portal_code: string | null;
  status: string;
  last_action: PortalAction;
  payload_hash: string | null;
  last_error: { message?: string; status?: number; [k: string]: unknown } | null;
  target_server: "TEST" | "PROD";
  triggered_by: string | null;
  created_at: string;
  updated_at: string;
}

export async function invokePortalPublish(args: {
  portal: PortalId;
  action: PortalAction;
  bienId: string;
  force?: boolean;
}): Promise<PortalPublishResponse> {
  const { data, error } = await supabase.functions.invoke<PortalPublishResponse>(
    "portal-publish",
    {
      body: {
        portal: args.portal,
        action: args.action,
        bien_id: args.bienId,
        force: args.force ?? false,
      },
    },
  );
  if (error) throw new Error(error.message ?? "portal-publish failed");
  if (!data) throw new Error("Réponse vide de portal-publish");
  return data;
}

/**
 * Récupère la dernière publication par portail pour un bien.
 * Renvoie un Record<PortalId, PortalPublicationRow | null>.
 *
 * On fait un seul SELECT puis on regroupe en mémoire — moins de round-trips.
 */
export async function fetchLatestPortalPublications(
  bienId: string,
): Promise<Record<PortalId, PortalPublicationRow | null>> {
  const { data, error } = await supabase
    .from("portal_publications")
    .select("*")
    .eq("bien_id", bienId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(error.message);

  const byPortal: Record<PortalId, PortalPublicationRow | null> = {
    website: null,
    immoweb: null,
    spotto: null,
    zimmo: null,
  };
  const grouped: Partial<Record<PortalId, PortalPublicationRow[]>> = {};
  for (const row of (data ?? []) as PortalPublicationRow[]) {
    (grouped[row.portal] ??= []).push(row);
  }
  for (const portal of Object.keys(grouped) as PortalId[]) {
    byPortal[portal] = currentPortalRow(grouped[portal] ?? []);
  }
  return byPortal;
}

/**
 * Row « courante » d'un bien pour un portail, parmi ses rows triées
 * created_at DESC : la plus récente, SAUF si c'est une dépublication /
 * suppression d'une AUTRE référence que la dernière publication. Cas réel
 * (2026-09-16) : GP_C6.6 publié sous « GP_C6.6-V », puis l'ancienne annonce
 * « GP_C6.6 » dépubliée par le nettoyage sandbox — la row la plus récente
 * est cette dépublication, mais l'annonce courante reste « GP_C6.6-V ».
 */
export function currentPortalRow<T extends Pick<PortalPublicationRow, "external_id" | "last_action" | "created_at">>(
  rowsDesc: readonly T[],
): T | null {
  const latest = rowsDesc[0];
  if (!latest) return null;
  const isOffline = (r: T) => r.last_action === "deactivate" || r.last_action === "delete";
  if (!isOffline(latest)) return latest;
  const lastPublish = rowsDesc.find((r) => r.last_action === "publish" || r.last_action === "update");
  if (!lastPublish || lastPublish.external_id === latest.external_id) return latest;
  // La référence de la dernière publication a-t-elle été dépubliée APRÈS elle ?
  // Une dépublication REFUSÉE ne retire rien : on ne la retient pas ici.
  const offlineSameRef = rowsDesc.find(
    (r) =>
      isOffline(r) &&
      r.external_id === lastPublish.external_id &&
      r.created_at > lastPublish.created_at &&
      (r as { status?: string }).status !== "REJECTED",
  );
  return offlineSameRef ?? lastPublish;
}

/** États finaux côté portail — UI peut arrêter de poller. */
export const PORTAL_FINAL_STATES = new Set([
  "PROCESSED",
  "REJECTED",
  "DEACTIVATED",
  "DELETED",
]);

/**
 * Fenêtre pendant laquelle un statut non final est « en cours » : alignée sur
 * POLL_LOOKBACK_HOURS (24h) de l'edge function `portal-status-poll`. Au-delà,
 * le cron n'interroge plus Immoweb, donc re-fetcher côté UI ne changerait rien
 * (5 lignes de test de juin 2026 sont restées en MEDIA_PROCESSING).
 */
export const PORTAL_INFLIGHT_MAX_AGE_MS = 24 * 60 * 60 * 1000;

export function isPortalInFlight(
  row: Pick<PortalPublicationRow, "status" | "created_at"> | null | undefined,
  now: number = Date.now(),
): boolean {
  if (!row || PORTAL_FINAL_STATES.has(row.status)) return false;
  const created = new Date(row.created_at).getTime();
  if (Number.isNaN(created)) return true;
  return now - created < PORTAL_INFLIGHT_MAX_AGE_MS;
}

/** Réduit des rows triées par created_at DESC à la row courante par bien (cf. currentPortalRow). */
export function latestByBien<
  T extends Pick<PortalPublicationRow, "bien_id" | "external_id" | "last_action" | "created_at">,
>(rows: T[]): Record<string, T> {
  const grouped: Record<string, T[]> = {};
  for (const row of rows) (grouped[row.bien_id] ??= []).push(row);
  const out: Record<string, T> = {};
  for (const [bienId, group] of Object.entries(grouped)) {
    const cur = currentPortalRow(group);
    if (cur) out[bienId] = cur;
  }
  return out;
}

/**
 * Dernière publication par bien pour UN portail (listes Publication). Un seul
 * SELECT sans `payload_snapshot` (le seul champ lourd) — quelques dizaines de
 * lignes par portail aujourd'hui, la limite est un garde-fou.
 */
export async function fetchLatestPortalPublicationsByBien(
  portal: PortalId,
): Promise<Record<string, PortalPublicationRow>> {
  const { data, error } = await supabase
    .from("portal_publications")
    .select(
      "id,bien_id,portal,external_id,reference_id,portal_code,status,last_action,payload_hash,last_error,target_server,triggered_by,created_at,updated_at",
    )
    .eq("portal", portal)
    .order("created_at", { ascending: false })
    .limit(5000);
  if (error) throw new Error(error.message);
  return latestByBien((data ?? []) as PortalPublicationRow[]);
}

/**
 * Texte lisible de `last_error` : Immoweb renvoie un TABLEAU d'erreurs
 * ({type, property, description, …}, ex. INVALID_DISTRIBUTIONTYPE_CHANGE),
 * l'edge function un objet {message, status} sur une erreur HTTP.
 */
export function portalErrorText(row: Pick<PortalPublicationRow, "last_error"> | null | undefined): string {
  const err = row?.last_error as unknown;
  if (!err) return "";
  const one = (e: unknown): string => {
    if (!e || typeof e !== "object") return e == null ? "" : String(e);
    const o = e as Record<string, unknown>;
    return String(o.description ?? o.message ?? o.type ?? "");
  };
  if (Array.isArray(err)) return err.map(one).filter(Boolean).join(" · ");
  return one(err);
}

/**
 * Dépublication (chantier 2026-09-14, feu vert Thibaud) : repasser un bien
 * Publié en Retiré/Brouillon — via le déroulant, via LOUÉ/VENDU (miroir du
 * trigger DB 065), ou via une mise sous option (miroir du trigger 084) — ne
 * notifiait JAMAIS les portails externes ; l'annonce restait live
 * indéfiniment côté Immoweb. Ces trois chemins écrivent tous
 * `statutPublication` puis passent par le même autosave — donc un seul point
 * d'accroche (l'`onSuccess` de la sauvegarde) couvre les trois. Extrait en
 * fonction pure (plutôt que directement dans BienDetailPage/BienVenteDetailPage)
 * pour être testable sans monter les deux pages.
 *
 * Symétrique côté republication (remonté par Thibaud le même jour : lever une
 * sous-option/republier via le déroulant ne repoussait pas non plus).
 *
 * @returns "publish" en entrant dans Publié, "deactivate" en en sortant, ou
 *   `null` si aucune des deux valeurs n'est "Publié" (rien à répercuter).
 */
export function portalActionForStatutChange(
  prevStatut: string | null | undefined,
  nextStatut: string | null | undefined,
): "publish" | "deactivate" | null {
  if (prevStatut === nextStatut) return null;
  const wasPublie = prevStatut === "Publié";
  const isPublie = nextStatut === "Publié";
  if (!wasPublie && !isPublie) return null;
  return isPublie ? "publish" : "deactivate";
}

/** Catégorie d'affichage pour le badge UI. */
export type PortalBadgeKind = "never" | "pending" | "success" | "error" | "deactivated" | "warning";

/**
 * Statut À AFFICHER pour une row, en tenant compte de l'action : Immoweb
 * répond PROCESSED aussi pour une dépublication ou une suppression traitée,
 * donc `status` seul afficherait « Publié » sur un bien dépublié (vu par
 * Thibaud le 2026-09-16 sur LS_3 mis sous option). `statusKey` = clé sous
 * `portals.status.*`.
 */
export function portalDisplayStatus(
  row: Pick<PortalPublicationRow, "status" | "last_action"> | null | undefined,
  /**
   * `statutPublication` du bien chez nous. Si l'annonce est en ligne côté
   * portail alors que le bien n'est PAS « Publié », c'est une annonce
   * fantôme (ex. les 30 tests sandbox du 30/06/2026 jamais dépubliés) : on
   * l'affiche en alerte « Encore en ligne » plutôt qu'en vert rassurant.
   */
  statutPublication?: string | null,
): { kind: PortalBadgeKind; statusKey: string } {
  if (!row) return { kind: "never", statusKey: "NEVER" };
  if (row.last_action === "deactivate" || row.last_action === "delete") {
    const deleting = row.last_action === "delete";
    if (row.status === "REJECTED") return { kind: "error", statusKey: "REJECTED" };
    if (PORTAL_FINAL_STATES.has(row.status)) {
      return { kind: "deactivated", statusKey: deleting ? "DELETED" : "DEACTIVATED" };
    }
    return { kind: "pending", statusKey: deleting ? "DELETING" : "DEACTIVATING" };
  }
  if (row.status === "PROCESSED" && statutPublication !== undefined && statutPublication !== "Publié") {
    return { kind: "warning", statusKey: "STILL_ONLINE" };
  }
  return { kind: badgeKindFor(row.status), statusKey: row.status };
}

/**
 * `null` (jamais publié) → "never" (gris "Non publié")
 * PROCESSED → success (vert)
 * REJECTED → error (rouge)
 * DEACTIVATED/DELETED → deactivated (gris)
 * Autres → pending (orange)
 */
export function badgeKindFor(status: string | null | undefined): PortalBadgeKind {
  if (!status) return "never";
  if (status === "PROCESSED") return "success";
  if (status === "REJECTED") return "error";
  if (status === "DEACTIVATED" || status === "DELETED") return "deactivated";
  return "pending";
}
